import React, { useState, useEffect } from "react";
import { Info, HelpCircle, ArrowRight } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

interface HelpDialogProps {
  featureId: string;
  title: string;
  description: string;
  videoUrl: string;
  triggerText?: string;
  icon?: "info" | "help";
  variant?: "default" | "outline" | "secondary" | "ghost";
  size?: "sm" | "default" | "lg" | "icon";
  className?: string;
}

const HelpDialog: React.FC<HelpDialogProps> = ({
  featureId,
  title,
  description,
  videoUrl,
  triggerText = "Help",
  icon = "info",
  variant = "outline",
  size = "default",
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const storageKey = `help-dialog-${featureId}`;

  useEffect(() => {
    const seen = localStorage.getItem(storageKey);
    if (!seen) {
      setIsOpen(true);
    }
  }, [storageKey]);

  const handleClose = () => {
    localStorage.setItem(storageKey, "true");
    setIsOpen(false);
  };

  const IconComponent = icon === "info" ? Info : HelpCircle;

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <AlertDialogTrigger asChild className={className}>
        <Button
          variant={variant}
          size={size}
          className={
            size == "icon"
              ? "size-7.5 text-muted-foreground hover:bg-muted-foreground/10"
              : ""
          }
        >
          <IconComponent />
          {triggerText && size !== "icon" && <span>{triggerText}</span>}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="max-w-4xl">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription className="text-left">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <iframe
          src={videoUrl}
          className="w-full aspect-video rounded-lg"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
          title={`${title} tutorial`}
        />
        <AlertDialogFooter>
          <Button
            onClick={handleClose}
            className="group"
            variant="neutral"
            size="sm"
          >
            Continue{" "}
            <ArrowRight className="group-hover:translate-x-0.5 transition-all" />
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default HelpDialog;
